import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';

const reviews = [
  {
    name: 'Klara B.',
    meta: '03/2026',
    text: 'Enno und Jens haben für unsere Physiotherapie HealthyMe GmbH die Website erstellt und gehostet. Die Zusammenarbeit war von Anfang bis Ende transparent und auf Augenhöhe. Wir wussten bei Kosten und Zeitplan immer genau, woran wir sind – absolut keine bösen Überraschungen. Sehr verlässlich und angenehm! Die Website spiegelt unsere Praxis perfekt wieder: modern, professionell und einladend. Besonders toll ist, dass die Website auch auf dem Smartphone super aussieht! Top Support, auch nach dem Livegang! Die Technik im Hintergrund läuft reibungslos und durch die SEO-Optimierung sind wir zukunftssicher aufgestellt. Insgesamt sehr empfehlenswerte Agentur!',
  },
  {
    name: 'Eliza T.',
    meta: '03/2026',
    text: 'Die Webseite wirkt modern und hochwertig, überzeugt durch starkes Webdesign und eine sehr gute Usability. Seit dem Relaunch verzeichnen wir spürbar mehr Buchungen. Genau das war unser Ziel.',
  },
  {
    name: 'Ralph B.',
    meta: '02/2026',
    text: "Perfekte und schnelle Hilfe. Alles aus einer Hand: Hosting, Technik, Content und schnelle Problemlösung, falls mein Computer streikt. Besser geht's nicht.",
  },
  {
    name: 'Jörg F.',
    meta: '03/2026',
    text: 'Sehr super Service, Danke.',
  },
  {
    name: 'Katja P.',
    meta: '03/2026',
    text: 'Super kompetent! Die beiden haben uns eine phantastische Homepage gestaltet, die nur positiven Anklang findet. Bei Problemen oder Änderungswünschen sind die beiden sehr gut erreichbar und antworten umgehend. Kann ich nur wärmstens empfehlen!',
  },
  {
    name: 'Tim B.',
    meta: '04/2026',
    text: 'Meine Homepage musste grundlegend erneuert werden. Vielen Dank an Enno & Jens für die durch und durch kompetente Unterstützung. Wünsche und Fragen meinerseits wurden verständlich und mit fachlichem Können erfolgreich zum Abschluss gebracht. Es ist tatsächlich nicht einfach Unternehmen mit Mitarbeitern zu finden, welche Kundenwünsche so schnell beantworten und auch umsetzen können. Vielen Dank dafür.',
  },
  {
    name: 'Matthias C.',
    meta: '04/2026',
    text: 'Eine echt coole und junge Firma, mit jeder Menge Know-How, Umsetzungskraft, Energie und frischen Ideen! Unsere Homepage haben wir maßgeschneidert, extrem schnell und mehr als gelungen mit der Firma gerelaunched – ein voller Erfolg für unseren Sportclub und seine Follower. Auch der aktuelle Servicesupport inkl. Wartung und Updatedienst funktioniert einfach hervorragend!\n\n• Partnerschaft & Vertrauen: Die Erstellung der Homepage wurde schnell und sehr gut umgesetzt, Kommunikation auf den Punkt, so wünscht man sich die Zusammenarbeit, von Beginn an. Ein engagiertes Team – dafür danken wir. Auch Änderungen und Wünsche werden flott und passig umgesetzt.\n\n• Design & Wirkung: Unsere Webseite wirkt modern und funktioniert für unsere Mitglieder. Das komplette Webdesign und die Usability sind sehr stimmig, unser Vereinsleben kommt hervorragend rüber, weshalb auch das Feedback durchweg positiv ist. Ein echter Gewinn für uns.\n\n• Know-how & Support: Durch die Zusammenarbeit sind wir digital optimal aufgestellt und werden bestens unterstützt. Die Tipps zur Pflege der Inhalte, die Implementierung neuer, sinnvoller Tools sowie die Suchmaschinenoptimierung (SEO) nutzen wir gerne. Technisch, grafisch, textlich und vor allem beim Support ist alles absolut stimmig.\n\nImmer wieder gern, wirklich sehr zu empfehlen; Deshalb weiter so!',
  },
  {
    name: 'Anna K.',
    meta: '04/2026',
    text: 'Wir sind sehr zufrieden mit der Zusammenarbeit! Die Agentur hat unsere Wünsche perfekt umgesetzt und uns mit Kreativität und Know-how überzeugt.\n\nKommunikation und Ablauf waren unkompliziert und zuverlässig. Das Ergebnis: modernes Design, starke Online-Präsenz und viel positives Feedback.\n\nKlare Empfehlung – wir freuen uns auf weitere Projekte!',
  },
];

const TEXT_LIMIT = 200;

const ReviewsSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [totalSlides, setTotalSlides] = useState(4);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const index = emblaApi.selectedScrollSnap();
    setSelectedIndex(index % totalSlides);
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi, totalSlides]);

  useEffect(() => {
    if (!emblaApi) return;
    
    const calculateSlides = () => {
      const snapCount = emblaApi.scrollSnapList().length;
      setTotalSlides(snapCount);
    };
    
    calculateSlides();
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('reInit', calculateSlides);
    
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
      emblaApi.off('reInit', calculateSlides);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const scrollToIndex = (index: number) => {
    emblaApi?.scrollTo(index);
  };

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">Kundenstimmen</span>
          <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mt-4 mb-6">
            Was sagen unsere Kunden über uns?
          </h2>
          <p className="text-lg text-muted-foreground">
            Ein Auszug aktueller Google-Rezensionen unserer Kunden und Partner. Besonders freut uns: Unsere bisherigen Google-Bewertungen liegen durchgehend bei 5 Sternen.
          </p>

          {/* Overall Rating */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-8 inline-block"
          >
            <div className="flex items-center gap-4 bg-gradient-to-r from-amber-500/10 to-amber-500/5 border border-amber-500/20 rounded-2xl px-8 py-4">
              <div className="flex items-center gap-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-amber-500 text-amber-500" aria-hidden="true" />
                ))}
              </div>
              <div className="text-left">
                <div className="text-3xl font-display font-bold text-foreground">5.0</div>
                <div className="text-sm text-muted-foreground">Durchschnitt (12 Bewertungen)</div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          {/* Carousel with Navigation */}
          <div className="relative">
            {/* Left Arrow */}
            <button
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className="absolute -left-5 md:-left-16 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 border border-primary/30 flex items-center justify-center text-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Vorherige Bewertungen"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Carousel */}
            <div ref={emblaRef} className="overflow-hidden">
              <div className="flex -ml-4 [&>*]:min-w-0 [&>*]:shrink-0 [&>*]:grow-0">
                {reviews.map((review, index) => (
                  <motion.div
                    key={`${review.name}-${index}`}
                    className="basis-full md:basis-1/2 pl-4"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <ReviewCard review={review} />
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Right Arrow */}
            <button
              onClick={scrollNext}
              disabled={!canScrollNext}
              className="absolute -right-5 md:-right-16 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 border border-primary/30 flex items-center justify-center text-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Nächste Bewertungen"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-center items-center gap-6 mt-12">
            {/* Dots Navigation */}
            <div className="flex justify-center gap-3">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollToIndex(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    index === selectedIndex
                      ? 'bg-primary w-8'
                      : 'bg-border hover:bg-muted-foreground'
                  }`}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Google Reviews Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center mt-12"
          >
            <a
              href="https://share.google/g09Xq6yoUG6s1mHIC"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg"
            >
              Alle Bewertungen auf Google ansehen
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ReviewCard = ({ review }: { review: typeof reviews[number] }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > TEXT_LIMIT;
  const displayText = isLong && !expanded ? review.text.slice(0, TEXT_LIMIT) + '…' : review.text;

  return (
    <article className="rounded-3xl border border-border bg-card p-8 shadow-lg h-full min-h-[350px] flex flex-col">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h3 className="text-2xl font-display font-bold text-card-foreground">{review.name}</h3>
          <p className="text-muted-foreground mt-1">{review.meta}</p>
        </div>
        <div className="flex items-center gap-1 text-amber-500">
          {Array.from({ length: 5 }).map((_, starIndex) => (
            <Star key={starIndex} className="w-5 h-5 fill-current" aria-hidden="true" />
          ))}
        </div>
      </div>
      <div className="flex-1">
        <p className="text-lg leading-relaxed text-muted-foreground">"{displayText}"</p>
        {isLong && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 inline-flex items-center gap-1 text-primary text-sm font-medium hover:underline"
          >
            {expanded ? 'Weniger anzeigen' : 'Mehr lesen'}
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
      </div>
    </article>
  );
};

export default ReviewsSection;
